import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLocale } from '../context/LocaleContext';
import { useProducts } from '../hooks/useProducts';
import { searchProducts } from '../lib/products';
import { cssBackgroundImage, getProductImageUrl } from '../utils/imageUrl';
import { SPORTS } from '../utils/sports';
import './SearchOverlay.css';

function SearchOverlay({ open, onClose }) {
  const [query, setQuery] = useState('');
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const { products, loading } = useProducts();
  const { formatMoney } = useLocale();

  const results = useMemo(
    () => searchProducts(products, query, 8),
    [products, query]
  );

  useEffect(() => {
    if (!open) {
      setQuery('');
      return undefined;
    }

    document.body.style.overflow = 'hidden';
    const timer = window.setTimeout(() => inputRef.current?.focus(), 40);

    const onKey = event => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);

    return () => {
      window.clearTimeout(timer);
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  const go = path => {
    onClose();
    navigate(path);
  };

  return (
    <div className="search-overlay" role="dialog" aria-modal="true" aria-label="Search">
      <button
        type="button"
        className="search-overlay-backdrop"
        aria-label="Close search"
        onClick={onClose}
      />
      <div className="search-overlay-panel">
        <form
          className="search-overlay-form"
          onSubmit={event => {
            event.preventDefault();
            const q = query.trim();
            if (!q) return;
            go(`/shop?q=${encodeURIComponent(q)}`);
          }}
        >
          <input
            ref={inputRef}
            type="search"
            className="search-overlay-input"
            placeholder="Search kits, teams, sports…"
            value={query}
            onChange={event => setQuery(event.target.value)}
            autoComplete="off"
          />
          <button type="button" className="search-overlay-close" onClick={onClose}>
            Close
          </button>
        </form>

        {!query.trim() && (
          <div className="search-overlay-section">
            <p className="search-overlay-label">Sports</p>
            <div className="search-overlay-sports">
              {SPORTS.map(sport => (
                <button
                  key={sport.slug}
                  type="button"
                  onClick={() => go(`/sports/${sport.slug}`)}
                >
                  {sport.name}
                </button>
              ))}
            </div>
          </div>
        )}

        {query.trim() && (
          <div className="search-overlay-section">
            <p className="search-overlay-label">
              {loading ? 'Searching…' : `${results.length} result${results.length === 1 ? '' : 's'}`}
            </p>
            {results.length === 0 && !loading ? (
              <p className="search-overlay-empty">No products match that search.</p>
            ) : (
              <ul className="search-overlay-results">
                {results.map(product => (
                  <li key={product.id}>
                    <button
                      type="button"
                      className="search-overlay-result"
                      onClick={() => go(`/product/${product.id}`)}
                    >
                      <span
                        className="search-overlay-thumb"
                        style={{
                          background: cssBackgroundImage(getProductImageUrl(product)),
                        }}
                      />
                      <span className="search-overlay-result-copy">
                        <span className="search-overlay-result-name">{product.name}</span>
                        <span className="search-overlay-result-price">
                          {formatMoney(
                            product.sale_price != null ? product.sale_price : product.price
                          )}
                        </span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {query.trim() && (
              <button
                type="button"
                className="store-link search-overlay-all"
                onClick={() => go(`/shop?q=${encodeURIComponent(query.trim())}`)}
              >
                View all in shop
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default SearchOverlay;
